import type {
  OrderTabletFlow,
  Tablet,
  TabletDeviceEvent,
  TabletEventRange,
  TabletIdentifiers,
  TabletLifecycleEvent
} from '@/types/types'
import axios from 'axios'

import useLocalStorage from './useLocalStorage'

const displayTabletCode = ref(false)

export default function useTablet() {
  const { t } = useI18n()
  const { user } = useLocalStorage()
  const notification = useNotification()

  const tabletEmptyState = ref({
    refreshButton: {
      text: t(`pages.emptyState.refresh`),
      icon: 'HeroArrowPathIconOutline',
      isOutlined: true,
      isLoading: false
    },
    noResultButton: {
      text: t(`pages.tablet.emptyState.button`),
      icon: 'HeroArrowTopRightOnSquareIconOutline',
      isOutlined: false,
      isLoading: false
    },
    title: t('pages.tablet.emptyState.title'),
    description: t('pages.tablet.emptyState.description'),
    icon: null
  })

  const getTablets = async (): Promise<Tablet[]> => {
    const { data } = await axios.get('tablets')
    return data.data
  }

  const getTabletsOfStore = async (storeId: number): Promise<Tablet[]> => {
    const { data } = await axios.get('tablets', { params: { storeId } })
    return data.data
  }

  const getTabletDetail = async (id: number): Promise<Tablet> => {
    const { data } = await axios.get(`/tablets/${id}/detail`)
    return data.data
  }

  const getTabletOrdersFlow = async (
    id: number,
    orderNumber?: string,
    range?: TabletEventRange
  ): Promise<OrderTabletFlow[]> => {
    const { data } = await axios.get(`/tablets/${id}/orders-flow`, {
      params: {
        ...(orderNumber ? { order_number: orderNumber } : {}),
        ...(range ? { range } : {})
      }
    })
    return data.data
  }

  const getTabletLifecycleEvents = async (
    id: number,
    around?: string,
    range?: TabletEventRange
  ): Promise<TabletLifecycleEvent[]> => {
    const { data } = await axios.get(`/tablets/${id}/lifecycle-events`, {
      params: { ...(around ? { around } : undefined), ...(range ? { range } : undefined) }
    })
    return data.data
  }

  const getTabletDeviceEvents = async (
    id: number,
    around?: string,
    range?: TabletEventRange
  ): Promise<TabletDeviceEvent[]> => {
    const { data } = await axios.get(`/tablets/${id}/device-events`, {
      params: { ...(around ? { around } : undefined), ...(range ? { range } : undefined) }
    })
    return data.data
  }

  const getTabletIdentifiers = async (): Promise<TabletIdentifiers> => {
    const { data } = await axios.get(`merchants/${user.value.merchant.id}/tablet-identifiers`)
    return data.data
  }

  const regenerateTabletIdentifiers = async (): Promise<string> => {
    try {
      const { data } = await axios.patch(`merchants/${user.value.merchant.id}/tablet-identifiers/regenerate-code`)
      if (data.data) {
        displayTabletCode.value = true
        notification.create('success', {
          text: t('pages.marketplace.tabletsPage.identity.notifications.success'),
          timeout: 3000
        })
        return data.data.code
      }
    } catch {
      notification.create('error', {
        text: t('pages.marketplace.tabletsPage.identity.notifications.error'),
        timeout: 3000
      })
    }
  }

  const deleteModalContent = (tabletToDelete: Tablet) => {
    return {
      title: t('pages.marketplace.tabletsPage.delete.title', { id: tabletToDelete.id }),
      subtitle: t('pages.marketplace.tabletsPage.delete.subtitle'),
      linkText: t('global.words.delete'),
      cancelText: t('global.words.cancel'),
      icon: 'HeroTrashIconSolid',
      bgColor: 'red'
    }
  }

  const deleteTablet = async (id: number) => {
    return await axios.delete(`/tablets/${id}`)
  }

  return {
    getTablets,
    getTabletsOfStore,
    getTabletDetail,
    getTabletOrdersFlow,
    getTabletLifecycleEvents,
    getTabletDeviceEvents,
    getTabletIdentifiers,
    regenerateTabletIdentifiers,
    deleteTablet,
    deleteModalContent,
    tabletEmptyState,
    displayTabletCode
  }
}
